import React, { useEffect, useState } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useSelector } from "react-redux";
import { selectIsDarkMode } from "@shared/slices/common.slice";
import { BulkCancelProgressData } from "@shared/types/common/jobs.types";
import { BulkProgressStats } from "common/ProgressToasts/BulkProgressStats";
import BulkProgressMinimizedIndicator from "common/ProgressToasts/BulkProgressMinimizedIndicator";
import { useRefValue } from "@shared/utils/hooks/misc.ts";

export interface BulkProgressJobToastProps {
  /** Called when the user closes the toast and the job is no longer active in Redux */
  onRemove?: () => void;
  isJobActiveInRedux?: () => boolean;
  notificationApiRef: React.RefObject<any>;
  toastKey: string;
  total: number;
  jobData: BulkCancelProgressData | null;
  isError?: boolean;
  titleComplete: string;
  titleInProgress: string;
  titleError?: string;
  errorMessage?: string;
  subtitle: string;
  successLabel: string;
  failedLabel: string;
  inactiveLabel?: string;
  alreadyClosedLabel?: string;
  extraContent?: React.ReactNode;
  /** For bots variant: trades data for the inner ring */
  tradesData?: BulkCancelProgressData | null;
  tradesTotal?: number;
  isTradesError?: boolean;
}

const BulkProgressJobToast: React.FC<BulkProgressJobToastProps> = ({
  notificationApiRef,
  toastKey,
  total,
  jobData,
  isError = false,
  titleComplete,
  titleInProgress,
  titleError = "Operation failed",
  errorMessage,
  subtitle,
  successLabel,
  failedLabel,
  inactiveLabel,
  alreadyClosedLabel,
  extraContent,
  tradesData,
  tradesTotal,
  isTradesError = false,
  onRemove,
  isJobActiveInRedux,
}) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const isDarkMode = useSelector(selectIsDarkMode);
  const [isMinimized, setIsMinimized] = useState(false);
  const isMinimizedRef = useRefValue(isMinimized);
  const onRemoveRef = useRefValue(onRemove);
  const isJobActiveInReduxRef = useRefValue(isJobActiveInRedux);

  const showToast = () => {
    setIsMinimized(false);
  };

  useEffect(() => {
    if (isMinimized) {
      notificationApiRef.current.destroy(toastKey);
      return;
    }

    if (isError) {
      notificationApiRef.current.error({
        key: toastKey,
        message: titleError,
        description: errorMessage ?? t("errors.generic"),
        placement: "bottomRight",
        duration: 5,
      });
      return;
    }

    if (!jobData) return;

    const success = jobData?.done ?? 0;
    const failed = jobData?.failed ?? 0;
    const inactive = jobData?.inactive_market ?? 0;
    const pending = jobData?.pending ?? 0;

    if (pending === 0 && success === 0 && failed === 0 && inactive === 0) {
      return;
    }

    const completed = success + failed + inactive;
    const isComplete = !pending && completed > 0 && completed >= total;
    const title = isComplete ? titleComplete : titleInProgress;

    const barColor = isComplete
      ? success === completed
        ? undefined
        : success === 0
        ? "#ff4d4f"
        : "#faad14"
      : "#1677ff";

    const description = (
      <div style={{ overflow: "hidden" }}>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: barColor ?? "#52c41a",
            borderRadius: "8px 8px 0 0",
          }}
        />
        <BulkProgressStats
          jobData={jobData}
          total={total}
          title={title}
          subtitle={subtitle}
          successLabel={successLabel}
          failedLabel={failedLabel}
          inactiveLabel={inactiveLabel}
          alreadyClosedLabel={alreadyClosedLabel}
        />
        {extraContent}
      </div>
    );

    notificationApiRef.current.open({
      key: toastKey,
      description,
      duration: 0,
      placement: "bottomRight",
      style: { paddingTop: 8 },
      onClose: () => {
        if (isMinimizedRef.current) return;
        const isActive = isJobActiveInReduxRef.current
          ? isJobActiveInReduxRef.current()
          : false;
        if (!isActive) {
          onRemoveRef.current?.();
        } else {
          setIsMinimized(true);
        }
      },
    });
  }, [
    jobData,
    isError,
    errorMessage,
    isMinimized,
    toastKey,
    titleComplete,
    titleInProgress,
    titleError,
    subtitle,
    total,
    successLabel,
    failedLabel,
    inactiveLabel,
    alreadyClosedLabel,
    extraContent,
    i18n.language,
    isDarkMode,
  ]);

  // Clicking the minimized indicator always restores the full toast
  const handleMinimizedClick = () => {
    showToast();
  };

  if (!isMinimized || isError) return null;
  if (!jobData) return null;

  return (
    <BulkProgressMinimizedIndicator
      jobData={jobData}
      total={total}
      isError={isError}
      tradesData={tradesData}
      tradesTotal={tradesTotal}
      isTradesError={isTradesError}
      onClick={handleMinimizedClick}
    />
  );
};

export default BulkProgressJobToast;
